import { useParams } from 'react-router-dom'

import { Features } from 'generated/featureDefinitions'
import moment from 'moment'

import { CONDITIONS_VALUES } from 'modules/core/components/MultiFilters/FilterItem/useFilterItem'
import type LogicOperatorForm from 'modules/core/components/MultiFilters/Forms/LogicOperatorForm'
import MultiFilterForm from 'modules/core/components/MultiFilters/Forms/MultiFilterForm'
import genericUseMultiFilters from 'modules/core/components/MultiFilters/hooks/useMultiFilters'
import genericApplyFilters, {
  type ExtraHooks,
} from 'modules/core/components/MultiFilters/hooks/utils/applyFilters'
import type { Employee as CurrentEmployee } from 'modules/core/hooks/useCurrentSession'
import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import { dateToString } from 'modules/core/lib/dates'
import { range } from 'modules/core/lib/frequencies'

import { InconsistencyState } from 'modules/attendance/components/ClockIn/types'
import { useGetAttendanceIncidentAggregateShiftLocations } from 'modules/attendance/hooks/shift_locations/useGetAggreagteShiftLocations'
import { useShiftInconsistencies } from 'modules/attendance/hooks/useShiftInconsistencies'
// NOTE: This is a workaround to load the clock in statuses only for time tracking page
//       MultiFilters requires a major refactor to remove this
import type { ClockInStatus } from 'modules/attendance/pages/CustomPeriodsPage'
import leaves from 'modules/timeoff/store/collections/leaves'

import employeeStatuses, { type EmployeeStatus } from './utils/employeeStatuses'
import employeeEvaluators from './utils/employeesEvaluators'

interface FilterOption {
  field: string
  value: string
}
export interface FilterOptions {
  skipDefaultFilters?: FilterOption[]
  clockInStatuses?: ClockInStatus[]
  newEntryFlowEnabled?: boolean
}

const filteringByStatus = (filters: MultiFilterForm[], status: EmployeeStatus): boolean =>
  filters.some(
    (filter) =>
      filter.get('field').value === 'employee_status' && filter.get('value')?.value === status
  )

const filteringByEmployeeAway = (filters: MultiFilterForm[]) =>
  filteringByStatus(filters, employeeStatuses.away)

const filteringByEmployeeAwayThisMonth = (filters: MultiFilterForm[]) =>
  filteringByStatus(filters, employeeStatuses.awayThisMonth)

const filteringByEmployeeActive = (filters: MultiFilterForm[]) =>
  filteringByStatus(filters, employeeStatuses.active)

const filteringByEmployeeTerminated = (filters: MultiFilterForm[]) =>
  filteringByStatus(filters, employeeStatuses.terminated)

const buildExtraHooks = (getFilterValue: any, options?: FilterOptions): ExtraHooks => {
  const DEFAULT_FILTERS = [
    new MultiFilterForm({
      condition: CONDITIONS_VALUES.isEqual,
      field: 'employee_status',
      value: employeeStatuses.active,
    }),
  ]

  return {
    beforeFilter: (filters: MultiFilterForm[]) => {
      if (filteringByEmployeeAway(filters)) {
        return leaves.fetch({
          data: {
            from: dateToString(moment().add(-1, 'day'), 'backend'),
            to: dateToString(moment().add(1, 'day'), 'backend'),
            approved: true,
          },
        })
      }

      if (filteringByEmployeeAwayThisMonth(filters)) {
        return leaves.fetch({
          data: {
            from: dateToString(moment().startOf('month'), 'backend'),
            to: dateToString(moment().endOf('month'), 'backend'),
            approved: true,
          },
        })
      }

      return Promise.resolve()
    },
    skipModelFilter: (filters: MultiFilterForm[], model: any) => {
      return (
        !getFilterValue(model, 'employee_status') &&
        (!filters.length || filteringByEmployeeActive(filters))
      )
    },
    getDefaultFilters: (filters: MultiFilterForm[]) => {
      if (filteringByEmployeeActive(filters) || filteringByEmployeeTerminated(filters)) {
        return null
      }

      if (!!options?.skipDefaultFilters?.length) {
        return (
          DEFAULT_FILTERS.filter((filter: MultiFilterForm) => {
            return !options.skipDefaultFilters?.some(
              (option) =>
                option.value === filter.get('value')?.value &&
                option.field === filter.get('field').value
            )
          }) ?? null
        )
      }

      return DEFAULT_FILTERS
    },
    customEvaluators: employeeEvaluators,
  }
}

export default function useEmployeeMultiFilters<T>(
  getFilterValue: (model: T, value: any) => any,
  localStorageKey: string,
  options?: FilterOptions
) {
  const {
    filteredModels,
    logicOperator,
    filters,
    onApplyFilters,
    onCancel,
    onRemoveFilter,
    onAddFilter,
    resetFilter,
  } = genericUseMultiFilters<T>(
    getFilterValue,
    localStorageKey,
    buildExtraHooks(getFilterValue, options),
    { newEntryFlowEnabled: options?.newEntryFlowEnabled }
  )

  const { featureAvailability: timeTrackingGeofencingAlertsAvailability } = useFeatureAvailability(
    Features.time_tracking_geofencing_alerts
  )

  const now = moment()

  const { aggregateShiftLocations } = useGetAttendanceIncidentAggregateShiftLocations(
    {
      startOn: dateToString(now.clone().startOf('month'), 'backend'),
      endOn: dateToString(now.clone().endOf('month'), 'backend'),
    },
    !timeTrackingGeofencingAlertsAvailability.available
  )

  const { day = now.date(), month, year = now.year(), periodType = 'daily' } = useParams()
  const currentMonth = month ? parseInt(month) : now.month()

  const dateRange = range(moment([year, currentMonth - 1, day]), periodType, true)
  const from = dateRange[0].format('YYYY-MM-DD')
  const to = dateRange[1].format('YYYY-MM-DD')

  const { data: shiftInconsistencies } = useShiftInconsistencies({
    dataType: '',
    startOn: from,
    endOn: to,
    state: InconsistencyState.pending,
  })

  const onApplyEmployeeFilters = async (models: T[], defaultFilter?: MultiFilterForm[]) => {
    await onApplyFilters(models, defaultFilter, {
      aggregateShiftLocationsIncidents: aggregateShiftLocations,
      clockInStatuses: options?.clockInStatuses ?? [],
      shiftInconsistencies,
    })
  }

  return {
    filteredModels,
    logicOperator,
    filters,
    onApplyFilters: onApplyEmployeeFilters,
    onCancel,
    onRemoveFilter,
    onAddFilter,
    resetFilter,
  }
}

function applyFilters<T>(
  models: T[],
  filters: MultiFilterForm[],
  logicOperator: LogicOperatorForm,
  getFilterValue: (model: T, value: any) => any,
  setFilteredModels: (...args: any[]) => any,
  currentEmployee: CurrentEmployee,
  allMyDescendants: number[]
) {
  return genericApplyFilters<T>(
    models,
    filters,
    logicOperator,
    getFilterValue,
    setFilteredModels,
    buildExtraHooks(getFilterValue),
    currentEmployee,
    allMyDescendants
  )
}
